import * as React from 'react'

import styled, { css } from 'styled-components'

import * as api from '@owl-nest/api-client/next'
import { renderConfetti } from '@owl-nest/confetti'
import * as hooks from '@owl-nest/hooks'
import { t } from '@owl-nest/localize'
import * as shadow from '@owl-nest/shadow'
import { SharingLinks } from '@owl-nest/sharing'
import * as service from '@owl-nest/services'
import * as plume from '@ulule/owl-kit-components/next'

type ShareModalProps = {
  closable?: boolean
  enableConfetti?: boolean
  enableWidget?: boolean
  modalButtons?: plume.ModalButtons
  modalPastille?: React.ReactElement
  modalHero?: React.ReactElement
  modalId?: 'ProjectDetailShare' | 'ProjectDetailLaunchShare'
  project: api.Project
  publicUser?: api.PublicUser
  sharingContext?: string
  trackingConfig: {
    category: shadow.CATEGORY
    source: string
  }
  withCallout?: boolean
}

const ProjectWidgetPreview = React.lazy(async () => ({ default: (await import('@ulule/duvet')).ProjectWidgetPreview }))

export function ShareProject(props: ShareModalProps) {
  const auth = service.user.useAuth()

  return <UserWiredShareProject {...props} publicUser={auth.type === 'loggedin' ? auth.user : props.publicUser} />
}

function UserWiredShareProject({
  closable = true,
  enableConfetti = false,
  enableWidget = true,
  modalButtons,
  modalHero = <plume.styles.heading.L>{t('Share project')}</plume.styles.heading.L>,
  modalId = 'ProjectDetailShare',
  modalPastille = <plume.illustrations.twoToned.Share size={plume.utils.isMobile() ? 52 : 120} />,
  project,
  publicUser,
  sharingContext = 'project-page',
  trackingConfig,
  withCallout = false,
}: ShareModalProps): React.ReactElement {
  const tracking = shadow.useTracking()

  const [isProjectDetailShareModalOpen, setIsProjectDetailShareModalOpen] = hooks.useModal(modalId)
  const [isWidgetOpen, setIsWidgetOpen] = React.useState(false)

  plume.hooks.useScrollLock(isProjectDetailShareModalOpen, { lockClassName: 'no-scroll' })

  React.useEffect(() => {
    if (enableConfetti && isProjectDetailShareModalOpen) {
      const canvas = document.getElementById('confettis-canvas')
      renderConfetti(canvas)
    }
  }, [isProjectDetailShareModalOpen])

  return (
    <ModalWithIcon
      buttons={modalButtons}
      closable={closable}
      onClose={() => {
        setIsProjectDetailShareModalOpen(false)
      }}
      open={isProjectDetailShareModalOpen}
      withCallout={withCallout}
      icon={modalPastille}
      svgScale={0.8}
    >
      <OverflowWrapper $isWidgetOpen={isWidgetOpen}>
        {enableConfetti && <CanvasConfetti id="confettis-canvas" />}

        {modalHero}

        <SharingLinks
          enableWidget={true}
          project={project}
          user={publicUser}
          sharingContext={sharingContext}
          sharingExtraData={{ eventCategory: trackingConfig.category, eventLabel: trackingConfig.source }}
          widget={
            <>
              <plume.Link
                withIcon
                className="lauch-share-modal-widget"
                onClick={() => {
                  setIsWidgetOpen(!isWidgetOpen)
                  track(shadow.SOCIAL_NETWORK.WIDGET)
                }}
              >
                <plume.glyphs.stroke.CodeBlock size={18} />
                {t('Embed widget')}
              </plume.Link>
            </>
          }
        />

        {enableWidget && (
          <React.Suspense fallback={<plume.Spinner />}>
            <Widget open={isWidgetOpen}>
              <ProjectWidgetPreview url={`${project.absolute_url}widget.html`} />
            </Widget>
          </React.Suspense>
        )}
      </OverflowWrapper>
    </ModalWithIcon>
  )

  function track(kind: shadow.SOCIAL_NETWORK): void {
    tracking.social.track({
      eventCategory: trackingConfig.category,
      eventLabel: trackingConfig.source,
      socialAction: 'click: Share',
      socialNetwork: kind,
      socialTarget: project.absolute_url,
    })
  }
}

const OverflowWrapper = styled.div<{ $isWidgetOpen: boolean }>`
  overflow: ${({ $isWidgetOpen }) => ($isWidgetOpen ? 'auto' : 'initial')};
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  max-height: 75vh;
`

const CanvasConfetti = styled.canvas`
  height: 170px;
  left: 0;
  position: absolute;
  top: 0;
  width: 100%;
  z-index: 0;
`

const ModalWithIcon = styled(plume.ModalWithIcon)<{ withCallout: boolean }>`
  ${plume.styles.modal.Body}:not(${plume.styles.modal.Actions}) {
    align-items: center;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding-bottom: 40px;
  }

  ${plume.styles.modal.Actions} button {
    width: fit-content;
  }

  ${plume.illustrations.Illustration} {
    position: relative;
    z-index: 1;
  }

  ${plume.styles.heading.L} {
    margin-top: 13px;
    margin-bottom: 32px;
    position: relative;
    z-index: 1;
  }

  ${plume.styles.copy.M.light} {
    margin-bottom: ${({ withCallout }) => (withCallout ? '20px' : '32px')};
  }

  ${plume.Link} {
    ${plume.glyphs.Icon} {
      margin-right: 5px;
    }
  }

  ${plume.glyphs.stroke.CodeBlock} {
    color: ${plume.COLORS.PRIMARY_SAND_500};
  }

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    ${plume.styles.copy.M.light} {
      margin-bottom: ${({ withCallout }) => (withCallout ? '20px' : '32px')};
      width: 546px;
    }

    ${plume.styles.modal.Body}:not(${plume.styles.modal.Actions}) {
      padding-bottom: 40px;
    }

    ${plume.styles.modal.Wrapper} {
      max-width: 734px;
      width: 100%;
      position: relative;

      // HACK: remove this when we correct the zindex issue on project page with all modales
      ${({ open }) => {
        if (open) {
          return css`
            max-height: 100%; //HACK: but don't remove this when we correct the zindex issue
          `
        }
      }}
    }

    ${plume.styles.copy.M.light} {
      width: 546px;
    }
  }
`

const Widget = styled.div<{ open: boolean }>`
  display: ${({ open }) => (open ? 'block' : 'none')};
  margin-top: 34px;
`
